


























import 'mapbox-gl/dist/mapbox-gl.css'
import { Component, Prop, Vue } from 'nuxt-property-decorator'
import mapboxgl, { GeoJSONSource } from 'mapbox-gl'
import Lot from '~/models/lot'
import { PublicProject } from '~/models/project'
import { getLotsStatus } from '~/api/consumer_map.api'
import { getDirections } from '~/api/mapbox.api'

@Component({
  components: {},
})
export default class DirectionsMap extends Vue {
  @Prop({ default: null }) lot: Lot
  @Prop({ default: null }) project: PublicProject

  map: mapboxgl.Map
  mapOptions: any = {
    container: 'direction-map',
    style: null,
    center: null,
    zoom: 15,
    antialias: true,
    doubleClickZoom: false,
    accessToken: null,
  }

  accessToken: string = null

  distance: number = 0
  duration: number = 0
  lotsStatus: any = null

  mounted() {
    this.init()
  }

  init() {
    this.accessToken = this.project.access_token
    this.mapOptions.accessToken = this.accessToken
    this.mapOptions.style = this.project.mapbox_url
    this.mapOptions.center = this.project.config.centerpoint_desktop

    // @ts-ignore
    this.map = new mapboxgl.Map({
      container: 'direction-map',
      ...this.mapOptions,
      center: {
        lng: this.mapOptions.center[0],
        lat: this.mapOptions.center[1],
      },
    })

    this.map.on('load', () => {
      this.mapInit()
    })
  }

  async mapInit() {
    if (!this.map) {
      return
    }

    this.lotsStatus = await getLotsStatus(this.$route.params.slug)

    this.setMapFilter('search_results', [])

    if (this.lotsStatus.available?.length > 0) {
      this.setMapFilter('available', this.lotsStatus.available)
    }
    if (this.lotsStatus.unavailable?.length > 0) {
      this.setMapFilter('unavailable', this.lotsStatus.unavailable)
    }
    if (this.lotsStatus.reserved?.length > 0) {
      this.setMapFilter('reserved', this.lotsStatus.reserved)
    }
    this.addLayer(
      'point',
      'circle',
      this.project.config.centerpoint_desktop,
      10,
      '#3887be'
    )
    this.addLayer(
      'end',
      'circle',
      [this.lot.longitude, this.lot.latitude],
      10,
      '#8B5CF6'
    )
    this.getDirections(this.lot.longitude, this.lot.latitude)
  }

  setMapFilter(mapLayer: string, lotStatus: Array<number>) {
    this.map?.setFilter(mapLayer, [
      'in',
      ['get', 'parcel_id'],
      ['literal', lotStatus],
    ])
  }

  addLayer(
    id: string,
    type: any,
    coordinates: Array<number>,
    pointRadius: number,
    pointColor: string
  ) {
    this.map.addLayer({
      id,
      type,
      source: {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'Point',
                coordinates,
              },
            },
          ],
        },
      },
      paint: {
        'circle-radius': pointRadius,
        'circle-color': pointColor,
      },
    })
  }

  getProjectCenterPoint() {
    return {
      longitude: this.project.config.centerpoint_desktop[0],
      latitude: this.project.config.centerpoint_desktop[1],
    }
  }

  sortAsc(a: number, b: number) {
    return a - b
  }

  private async getDirections(longitude: number, latitude: number) {
    try {
      const res = await getDirections(
        this.getProjectCenterPoint().longitude,
        this.getProjectCenterPoint().latitude,
        longitude,
        latitude,
        this.accessToken
      )
      const {
        routes: [
          {
            distance,
            duration,
            geometry: { coordinates: route },
          },
        ],
      } = res

      const geojson: any = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: route,
        },
      }

      if (this.map.getSource('route')) {
        const routeSource: GeoJSONSource = this.map.getSource(
          'route'
        ) as GeoJSONSource
        routeSource.setData(geojson)
      } else {
        this.map.addLayer({
          id: 'route',
          type: 'line',
          source: {
            type: 'geojson',
            data: {
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: geojson,
              },
            },
          },
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
          paint: {
            'line-color': '#3887be',
            'line-width': 5,
            'line-opacity': 0.75,
          },
        })

        const routeSource: GeoJSONSource = this.map.getSource(
          'route'
        ) as GeoJSONSource
        routeSource.setData(geojson)
      }

      const [w, e] = [this.getProjectCenterPoint().longitude, longitude].sort(
        this.sortAsc
      )

      const [s, n] = [this.getProjectCenterPoint().latitude, latitude].sort(
        this.sortAsc
      )

      const routeBounds = route.reduce(
        ([[w, s], [e, n]]: any[], [lng, lat]: any[]) => {
          if (lat < s) s = lat
          if (lat > n) n = lat

          if (lng < w) w = lng
          if (lng > e) e = lng

          return [
            [w, s],
            [e, n],
          ]
        },
        [
          [w, s],
          [e, n],
        ]
      )

      this.map.fitBounds(routeBounds, {
        padding: { top: 25, bottom: 25, left: 15, right: 15 },
      })

      this.distance = distance
      this.duration = duration
    } catch (err) {
      console.error(err)
    }
  }
}
